/**
 * Symbol registry for camera plans: every symbol that can be placed on a geo
 * plan, and the one set of camera types used by image, embed and geo plans.
 *
 * Glyphs are 24×24 stroke paths (stroke-width 1.9, round caps/joins), drawn
 * in INK on a round chip filled with the symbol's colour.
 */
export const SYMBOL_COLORS = {
    amber: "#FFB224",
    cyan: "#3FD3EA",
    violet: "#C39BFF",
    pale: "#E9EDF2",
};
/** Glyph and outline colour on chips. */
export const SYMBOL_INK = "#10161D";
export const PALETTE_GROUPS = [
    { key: "cameras", label: "Cameras" },
    { key: "air", label: "Air, rail and cable" },
    { key: "venue", label: "Venue" },
    { key: "draw", label: "Drawing" },
];
export const CAMERA_TYPES = [
    "fixed",
    "ptz",
    "handheld",
    "ssm",
    "jib",
    "ebike",
    "moto",
    "drone",
    "fpv",
    "helicopter",
    "rail",
    "cable",
];
export const INFRA_TYPES = [
    "fibre",
    "power",
    "tv_compound",
    "rf",
    "commentary",
    "mixed_zone",
    "podium",
    "hub",
    "internet",
    "tower",
    "split",
];
/** Generic elements styled per item (colour, dash, size, icon). */
export const DRAW_TYPES = ["line", "text", "marker"];
const { amber, cyan, violet, pale } = SYMBOL_COLORS;
const GLYPHS = {
    fixed: '<rect x="4" y="4" width="11" height="7" rx="1"/><path d="M15 6l5-2v7l-5-2M10 11v3M10 14l-4 7M10 14l4 7"/>',
    ptz: '<path d="M6 10c1.5-3 4-4.5 6-4.5s4.5 1.5 6 4.5c-1.5 3-4 4.5-6 4.5S7.5 13 6 10Z"/><circle cx="12" cy="10" r="2"/><path d="M9 18h6M12 14v4M8 21h8"/>',
    handheld: '<rect x="3" y="6" width="12" height="8" rx="1.3"/><path d="M15 8.5l5-2.5v8l-5-2.5M7 14v6h4v-6"/>',
    ssm: '<path d="M19 12a7 7 0 1 1-2.2-5.1M19.5 3.5V8h-4.5"/><circle cx="12" cy="12" r="2.3" fill="currentColor"/>',
    jib: '<path d="M3 20L17 9M3 21h7M7 17v4"/><rect x="16" y="4" width="6" height="6" rx="1"/>',
    ebike: '<circle cx="5.5" cy="16.5" r="3.5"/><circle cx="18.5" cy="16.5" r="3.5"/><path d="M5.5 16.5l4-7.5h6l3 7.5M9.5 9l3 7.5h-7M15.5 9l-1-2.5h2.5"/><path d="M10.5 2.5l-2 3h2.4l-1.4 3" stroke-width="1.5"/>',
    moto: '<circle cx="5" cy="17" r="3"/><circle cx="19" cy="17" r="3"/><path d="M5 17l3.5-6h5.5l2 3.5M8.5 11l-1.5-2.5H4M14 11l1.5-3H19l1 2M14 17h-5l-.5-3"/>',
    drone: '<circle cx="5" cy="5" r="2.4"/><circle cx="19" cy="5" r="2.4"/><circle cx="5" cy="19" r="2.4"/><circle cx="19" cy="19" r="2.4"/><path d="M7 7l10 10M17 7L7 17"/><rect x="9.5" y="9.5" width="5" height="5" rx="1" fill="currentColor"/>',
    fpv: '<path d="M12 3l8 18-8-4.5L4 21z"/>',
    helicopter: '<path d="M4 8.5h16M12 5v3.5M7 11.5h7.5a3.5 3.5 0 0 1 0 7H9a3 3 0 0 1-3-3v-1l-2-1M15 12l4-2v2l-4 1M8.5 18.5l-1.5 2M13.5 18.5l1.5 2M6 20.5h10"/>',
    rail: '<path d="M5 3v18M19 3v18M5 7h14M5 12h14M5 17h14"/>',
    cable: '<path d="M3 5l18 4"/><circle cx="12" cy="7.2" r="2.2"/><path d="M12 9.5v6"/><rect x="8.5" y="15.5" width="7" height="5" rx="1"/>',
    fibre: '<path d="M3 17c5 0 4-10 9-10s4 10 9 10" stroke-dasharray="2.5 2.5"/><circle cx="3" cy="17" r="1.4" fill="currentColor"/><circle cx="21" cy="17" r="1.4" fill="currentColor"/>',
    power: '<path d="M13 2.5L5.5 13H11l-1 8.5L18.5 10H13z"/><path d="M2 21h2M20 21h2" stroke-dasharray="1 2.5"/>',
    tv_compound: '<path d="M2 7h12v10H2zM14 10h4l4 3.5V17h-8z"/><circle cx="6" cy="18.5" r="1.9"/><circle cx="17.5" cy="18.5" r="1.9"/>',
    rf: '<path d="M12 21v-9"/><circle cx="12" cy="10" r="1.6" fill="currentColor"/><path d="M7.6 6.6a6.2 6.2 0 0 0 0 6.8M16.4 6.6a6.2 6.2 0 0 1 0 6.8M4.6 4a10.4 10.4 0 0 0 0 12M19.4 4a10.4 10.4 0 0 1 0 12"/>',
    commentary: '<path d="M5 14v-2a7 7 0 0 1 14 0v2M4 14h3v5H4zM17 14h3v5h-3zM19 19c0 2.2-3 2.2-6 2.2"/>',
    mixed_zone: '<rect x="9" y="3" width="6" height="11" rx="3"/><path d="M6 11a6 6 0 0 0 12 0M12 17v4M9 21h6"/>',
    podium: '<path d="M3 20.5V15h6v-4h6v7h6v2.5z"/>',
    hub: '<path d="M13 3L5 13.5h6L10 21l8-10.5h-6z"/>',
    internet: '<circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3c3.2 3 3.2 15 0 18M12 3c-3.2 3-3.2 15 0 18"/>',
    tower: '<path d="M6 21L9 3h6l3 18M7.6 12h8.8M6.8 16.6h10.4M8.4 7.4h7.2"/>',
    split: '<circle cx="12" cy="14" r="7"/><path d="M12 14v-4M9.5 3h5M12 3v4"/>',
    line: '<path d="M4.5 19.5L19.5 4.5"/><circle cx="4.5" cy="19.5" r="1.6" fill="currentColor"/><circle cx="19.5" cy="4.5" r="1.6" fill="currentColor"/>',
    text: '<path d="M5 7V4.5h14V7M12 4.5v15M9 19.5h6"/>',
    marker: '<path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/><circle cx="12" cy="10" r="3"/>',
};
function camera(key, label, shortLabel, paletteGroup, color, extra = {}) {
    return {
        key,
        label,
        shortLabel,
        paletteGroup,
        color,
        kind: "point",
        isCamera: true,
        glyph: GLYPHS[key],
        ...extra,
    };
}
function infra(key, label, paletteGroup, color, labelPrefix, extra = {}) {
    return {
        key,
        label,
        shortLabel: label,
        paletteGroup,
        color,
        kind: "point",
        isCamera: false,
        glyph: GLYPHS[key],
        labelPrefix,
        numbered: true,
        ...extra,
    };
}
function draw(key, label, kind, labelPrefix) {
    return {
        key,
        label,
        shortLabel: label,
        paletteGroup: "draw",
        color: pale,
        kind,
        isCamera: false,
        glyph: GLYPHS[key],
        labelPrefix,
        numbered: false,
        drawing: true,
    };
}
/** Where a camera shows on the elevation profile. */
export const PROFILE_DISPLAYS = ["mark", "lane"];
/** Every symbol type, in palette order. */
export const SYMBOL_TYPES = [
    camera("fixed", "Fixed camera", "Fixed", "cameras", amber, {
        viewArea: { fovDeg: 28, reachM: 180 },
    }),
    camera("ptz", "PTZ camera", "PTZ", "cameras", amber, {
        viewArea: { fovDeg: 40, reachM: 150 },
    }),
    camera("handheld", "Handheld / Steadicam", "Handheld", "cameras", amber, {
        viewArea: { fovDeg: 42, reachM: 80 },
    }),
    camera("ssm", "Super slow-motion", "Super slow-mo", "cameras", amber, {
        viewArea: { fovDeg: 16, reachM: 250 },
    }),
    camera("jib", "Jib / crane", "Jib", "cameras", amber, {
        viewArea: { fovDeg: 46, reachM: 120 },
    }),
    camera("ebike", "E-bike cam", "E-bike", "cameras", amber, {
        viewArea: { fovDeg: 60, reachM: 120 },
        profileLane: true,
        ridesRoute: true,
    }),
    camera("moto", "Motorbike cam", "Motorbike", "cameras", amber, {
        viewArea: { fovDeg: 60, reachM: 150 },
        profileLane: true,
        ridesRoute: true,
    }),
    camera("drone", "Drone", "Drone", "air", cyan, {
        viewArea: { fovDeg: 70, reachM: 300 },
        coversRoute: true,
        profileLane: true,
    }),
    camera("fpv", "FPV drone", "FPV", "air", cyan, {
        viewArea: { fovDeg: 64, reachM: 200 },
        coversRoute: true,
        profileLane: true,
    }),
    camera("helicopter", "Helicopter", "Helicopter", "air", cyan, {
        viewArea: { fovDeg: 50, reachM: 800 },
        coversRoute: true,
        profileLane: true,
    }),
    camera("rail", "Rail cam", "Rail", "air", cyan, {
        kind: "line",
        lineStyle: "rail",
        coversRoute: true,
        profileLane: true,
    }),
    camera("cable", "Cable cam", "Cable", "air", cyan, {
        kind: "line",
        lineStyle: "cable",
        coversRoute: true,
        profileLane: true,
    }),
    infra("fibre", "Fibre / cable route", "air", violet, "FIBRE", {
        kind: "line",
        lineStyle: "fibre",
    }),
    infra("power", "Power route", "air", violet, "POWER", {
        kind: "line",
        lineStyle: "power",
    }),
    infra("tv_compound", "TV compound", "venue", pale, "TV COMPOUND", {
        numbered: false,
    }),
    infra("rf", "RF receive point", "venue", violet, "RF"),
    infra("commentary", "Commentary position", "venue", pale, "COMM"),
    infra("mixed_zone", "Mixed zone / interviews", "venue", pale, "MIXED ZONE", {
        numbered: false,
    }),
    infra("podium", "Podium / flower ceremony", "venue", pale, "PODIUM", {
        numbered: false,
    }),
    infra("hub", "Power / fibre hub", "venue", pale, "HUB"),
    infra("internet", "Internet connection", "venue", pale, "INTERNET"),
    infra("tower", "Camera tower", "venue", pale, "TOWER"),
    infra("split", "Split / timing point", "venue", pale, "SPLIT"),
    draw("line", "Line", "line", ""),
    draw("text", "Text", "point", "Text"),
    draw("marker", "Icon with text", "point", ""),
];
const SYMBOLS_BY_KEY = new Map(SYMBOL_TYPES.map((symbol) => [symbol.key, symbol]));
/** Legacy `cameras.icon` values, rewritten once by the geo migration. */
const LEGACY_CAMERA_ICONS = {
    tripod: "fixed",
    robotic: "ptz",
    shoulder: "handheld",
    steadicam: "handheld",
    crane: "jib",
};
export function isCameraType(key) {
    return CAMERA_TYPES.includes(key);
}
/** Resolve a camera icon, mapping legacy values and falling back to fixed. */
export function resolveCameraType(icon) {
    if (icon && isCameraType(icon))
        return icon;
    return (icon && LEGACY_CAMERA_ICONS[icon]) || "fixed";
}
export function getSymbolType(key) {
    return ((key && SYMBOLS_BY_KEY.get(key)) ||
        SYMBOLS_BY_KEY.get(resolveCameraType(key)));
}
/** Camera types in palette order, for pickers. */
export const CAMERA_SYMBOL_TYPES = SYMBOL_TYPES.filter((s) => s.isCamera);
/** Full SVG markup of a glyph, e.g. for an <img> or a canvas. */
export function glyphSvg(key, { size = 24, color = SYMBOL_INK } = {}) {
    const symbol = getSymbolType(key);
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" color="${color}">${symbol.glyph}</svg>`;
}
/** Full SVG markup of a round chip with its glyph (white ring, dark outline). */
export function chipSvg(key, size = 30) {
    const symbol = getSymbolType(key);
    const r = 12;
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="-15 -15 30 30"><circle r="${r * 1.09}" fill="none" stroke="rgba(0,0,0,.5)" stroke-width="${r * 0.18}"/><circle r="${r}" fill="${symbol.color}" stroke="#fff" stroke-width="${r * 0.17}"/><g transform="translate(-8.7 -8.7) scale(0.725)" fill="none" stroke="${SYMBOL_INK}" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" color="${SYMBOL_INK}">${symbol.glyph}</g></svg>`;
}
/** "7" → "CAM 7"; non-numeric camera numbers are shown as they are. */
export function cameraDisplayName(cameraNumber) {
    const trimmed = cameraNumber.trim();
    return /^\d+$/.test(trimmed) ? `CAM ${trimmed}` : trimmed;
}
/**
 * Label of a plan item on the map and in lists. Camera positions get their
 * letter only when the camera has more than one position.
 */
export function itemDisplayLabel(item, camera, positionCount = 1) {
    if (camera) {
        const name = cameraDisplayName(camera.number);
        return positionCount > 1 && item.positionLetter
            ? `${name} ${item.positionLetter}`
            : name;
    }
    return item.label?.trim() || getSymbolType(item.symbolType).label;
}
/** Next label for a new plan-only item, e.g. "RF 3" when "RF 1/2" exist. */
export function nextItemLabel(symbolType, existingLabels) {
    const symbol = getSymbolType(symbolType);
    // Drawn lines and markers start without a label
    if (symbol.drawing)
        return symbol.labelPrefix || null;
    const prefix = symbol.labelPrefix ?? symbol.label.toUpperCase();
    if (!symbol.numbered)
        return prefix;
    const pattern = new RegExp(`^${prefix.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")} (\\d+)`);
    const max = existingLabels.reduce((acc, label) => {
        const match = (label ?? "").match(pattern);
        return match ? Math.max(acc, Number(match[1])) : acc;
    }, 0);
    return `${prefix} ${max + 1}`;
}
/** Next free position letter of a camera: A, B, … Z, AA, AB … */
export function nextPositionLetter(used) {
    const taken = new Set(used.filter(Boolean));
    for (let i = 0; i < 26 * 27; i++) {
        const letter = i < 26
            ? String.fromCharCode(65 + i)
            : String.fromCharCode(64 + Math.floor(i / 26)) +
                String.fromCharCode(65 + (i % 26));
        if (!taken.has(letter))
            return letter;
    }
    throw new Error("No free position letter");
}
/** Next camera number of a plan: one more than the highest numeric suffix. */
export function nextCameraNumber(numbers) {
    const max = numbers.reduce((acc, value) => {
        const match = value.match(/\d+$/);
        return match ? Math.max(acc, Number(match[0])) : acc;
    }, 0);
    return String(max + 1);
}
/**
 * Where a camera shows on the elevation profile: its own setting, else a
 * lane for long range and moving camera types and a mark for the rest.
 */
export function cameraProfileDisplay(camera) {
    if (camera.profileDisplay)
        return camera.profileDisplay;
    return getSymbolType(camera.icon).profileLane ? "lane" : "mark";
}
